/**
 * What kind of machine is this, and what should it start at?
 *
 * Two questions, one file, because they are answered from the same handful of signals and getting
 * either wrong is a bad first thirty seconds: a phone that opens with a keyboard hint strip and no
 * stick cannot be flown at all, and a phone that opens at the Medium tier spends its first ten
 * seconds at nine frames a second before the governor rescues it.
 *
 * ## Why `maxTouchPoints` alone is the wrong test
 *
 * Every touchscreen laptop reports touch points, and a good number of desktop monitors do. Asking
 * "can this device be touched" and concluding "this is a phone" is how a Surface or an XPS ends up
 * with a virtual joystick over a perfectly good keyboard. The question that actually matters is
 * whether touch is the *primary* pointer, and CSS answers it precisely: `pointer: coarse` means
 * the primary pointer is imprecise, and `hover: none` means it cannot hover. A touchscreen laptop
 * has a mouse as its primary pointer and reports `fine` and `hover`, whatever its screen can do.
 *
 * All three are required together, so a device has to be touch-capable, imprecise *and* unable to
 * hover before the touch tier appears. That is a phone or a tablet and essentially nothing else.
 *
 * ## Why the tier starts low rather than being guessed
 *
 * There is no reliable way to ask a browser how fast its GPU is. `deviceMemory` is coarse and
 * absent on iOS, `hardwareConcurrency` counts efficiency cores, and the renderer's adapter info is
 * masked. Every heuristic built on them is a guess that is wrong for some real phone.
 *
 * So this does not guess: on a touch-primary device it starts at **Low** and lets the quality
 * governor climb. That is not pessimism, it is the asymmetry — starting low on a fast phone costs
 * ten seconds of slightly plainer sky before the governor steps up, whereas starting high on a
 * slow one costs a session that stutters, thermally throttles, and is judged in the first ten
 * seconds. The governor is the measurement; this is only where it begins.
 *
 * `updateFixed` / `updateFrame`: neither. Read once during bootstrap.
 */

import type { QualityTier } from './quality.js';

/** Everything the decisions below are allowed to look at. Injected, so the tests are about logic. */
export interface PlatformSignals {
  /** `navigator.maxTouchPoints`. */
  maxTouchPoints: number;
  /** `matchMedia('(pointer: coarse)').matches` — the *primary* pointer is imprecise. */
  coarsePointer: boolean;
  /** `matchMedia('(hover: none)').matches` — the primary pointer cannot hover. */
  hoverNone: boolean;
  /** `?touch=1` forces the touch tier on, `?touch=0` forces it off. Null when unset. */
  forced: boolean | null;
}

/** Read the signals off this browser. The only function here that touches a global. */
export function readPlatformSignals(
  search: string = globalThis.location?.search ?? '',
): PlatformSignals {
  const media = (query: string): boolean => globalThis.matchMedia?.(query).matches ?? false;
  return {
    maxTouchPoints: globalThis.navigator?.maxTouchPoints ?? 0,
    coarsePointer: media('(pointer: coarse)'),
    hoverNone: media('(hover: none)'),
    forced: requestedTouchTier(search),
  };
}

/**
 * `?touch=1` — put the touch controls on a desktop, `?touch=0` — take them off a phone.
 *
 * Both directions matter. The first is how the touch tier is developed and reviewed at all without
 * a phone in hand; the second is the escape hatch for a tablet with a keyboard case, where the
 * detection is right about the hardware and wrong about how it is being used.
 */
export function requestedTouchTier(search: string = globalThis.location?.search ?? ''): boolean | null {
  const value = new URLSearchParams(search).get('touch');
  if (value === null) return null;
  return value !== '0' && value.toLowerCase() !== 'false';
}

/** Is touch the primary way this device is pointed at? */
export function isTouchPrimary(signals: PlatformSignals): boolean {
  if (signals.forced !== null) return signals.forced;
  return signals.maxTouchPoints > 0 && signals.coarsePointer && signals.hoverNone;
}

/**
 * The tier a session should open at, before the governor has an opinion.
 *
 * `medium` on a desktop is the shipped default and CLAUDE.md's reference point. `low` on anything
 * touch-primary, for the asymmetry argued in the header.
 */
export function startTierFor(signals: PlatformSignals): QualityTier {
  return isTouchPrimary(signals) ? 'low' : 'medium';
}

/**
 * Is the device being held the wrong way up?
 *
 * Only ever true on a touch-primary device: a desktop window can legitimately be taller than it is
 * wide, and telling somebody to rotate their monitor is not useful advice. Measured from the
 * viewport rather than `screen.orientation`, because a phone in a split-screen or a
 * picture-in-picture is landscape by the sensor and portrait by the space the canvas actually has,
 * and the space is what makes an instrument panel unreadable.
 */
export function shouldPromptForLandscape(
  signals: PlatformSignals,
  viewportWidthPx: number,
  viewportHeightPx: number,
): boolean {
  if (!isTouchPrimary(signals)) return false;
  return viewportHeightPx > viewportWidthPx;
}
